/**
 * STO-354/STO-359 — a porta para o aviso do SISTEMA OPERACIONAL.
 *
 * `new Notification()` é proibido nesta casa: no Chrome de Android o
 * construtor lança `TypeError: Illegal constructor`, e o tablet do técnico de
 * campo é Android. `ServiceWorkerRegistration.showNotification()` é a única
 * via que atende mesa e celular com um só caminho de código (MDN, "Using the
 * Notifications API").
 *
 * A permissão só pode ser pedida por gesto direto do usuário — nunca no boot.
 * `pedirPermissaoDeAviso` existe só para ser chamada de dentro de um handler
 * de clique (ver `SettingsModal`).
 */
import { deveAvisarForaDaTela } from './deve-avisar-fora-da-tela'

export interface AvisoDoSistema {
  titulo: string
  corpo: string
  /** Id do alerta/trabalho. Duas abas escondidas colapsam num banner só. */
  tag: string
  /** Para onde o clique leva, dentro do app. Ex.: '/chat/conv-123'. */
  url: string
  critico: boolean
}

function suportaAvisoDoSistema(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && typeof Notification !== 'undefined'
}

export async function registrarServiceWorkerDeAviso(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  try {
    await navigator.serviceWorker.register('/sw.js')
  } catch (err) {
    console.error('[os-notification] falha ao registrar o service worker', err)
  }
}

export async function pedirPermissaoDeAviso(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied'
  return Notification.requestPermission()
}

/** `false` = não avisou (e não era para avisar). NUNCA lança. */
export async function avisarForaDaTela(aviso: AvisoDoSistema): Promise<boolean> {
  const suporta = suportaAvisoDoSistema()
  const permissao = suporta ? Notification.permission : 'denied'
  const deveAvisar = deveAvisarForaDaTela({
    visibilityState: document.visibilityState,
    permissao,
    suporta,
  })
  if (!deveAvisar) return false

  try {
    const reg = await navigator.serviceWorker.ready
    await reg.showNotification(aviso.titulo, {
      body: aviso.corpo,
      tag: aviso.tag,
      icon: '/favicon.svg',
      data: { url: aviso.url },
      requireInteraction: aviso.critico,
    })
    return true
  } catch (err) {
    console.error('[os-notification] falha ao mostrar o aviso', err)
    return false
  }
}
